import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { refreshAuthToken } from '~/api/refresh-token';
import { useAuthStore } from '~/store/auth.store';

import { createDeliveryRunStream, type DeliveryStreamEvent } from '../services/delivery-run-stream';
import { getDeliveryRun } from '../services/delivery-runs.service';
import type { DeliveryRunDetail, JsonMap } from '../types/delivery-runs.types';

/** How the run view is currently being kept fresh. */
export type RunStreamStatus = 'idle' | 'connecting' | 'live' | 'fallback';

/** Reconnect backoff: 1s, 2s, 4s, 8s (jittered), then give up and let polling take over. */
const MAX_RECONNECT_ATTEMPTS = 4;
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 8000;
/** A stream open at least this long counts as stable and earns a fresh reconnect budget. */
const MIN_STABLE_CONNECTION_MS = 10_000;
/** Upper bound on a 429 Retry-After so a misconfigured server can't park the stream. */
const RATE_LIMIT_RETRY_AFTER_CAP_MS = 30_000;
/** Timeline/tree reads are invalidated at most this often while frames arrive. */
const SIDE_INVALIDATE_MS = 5000;

/**
 * Run fields a progress frame may carry. Only these are merged into the cached
 * detail, so an unknown frame shape can never inject garbage into the run.
 */
const MERGEABLE_KEYS: (keyof DeliveryRunDetail)[] = [
  'status',
  'phase',
  'isTerminal',
  'percentComplete',
  'totals',
  'currentStage',
  'timing',
  'signals',
  'startedAt',
  'completedAt',
  'durationSeconds',
  'errorCode',
  'errorMessage',
  'availableActions',
  'capabilities',
  'lineage',
];

/** ±50% jitter so simultaneously disconnected clients don't reconnect in lockstep. */
function jitter(baseMs: number): number {
  return baseMs * (0.5 + Math.random());
}

function isRateLimitError(err: unknown): err is { status: 429; retryAfterSeconds?: number } {
  return typeof err === 'object' && err !== null && (err as { status?: number }).status === 429;
}

function isAuthError(err: unknown): boolean {
  const status = (err as { status?: number }).status;
  return status === 401 || status === 403;
}

/**
 * Find the run object inside a frame. Frames may be the run itself, an
 * `{ data: run }` envelope, or `{ run: run }`; anything else is treated as a
 * flat partial. A frame naming a different run id is ignored.
 */
function extractRunPayload(data: JsonMap, runId: string): JsonMap | null {
  const candidates: unknown[] = [data.data, data.run, data];
  for (const c of candidates) {
    if (typeof c === 'object' && c !== null && !Array.isArray(c)) {
      const obj = c as JsonMap;
      if (typeof obj.id === 'string' && obj.id !== runId) return null;
      return obj;
    }
  }
  return null;
}

/**
 * Merge a progress frame into the cached run detail. Returns the merged run
 * (or null when nothing was cached yet — a partial frame must never fabricate
 * a detail object the page would then render as complete).
 */
function applyFrame(
  queryClient: QueryClient,
  runId: string,
  event: DeliveryStreamEvent,
): DeliveryRunDetail | null {
  const payload = extractRunPayload(event.data, runId);
  if (!payload) return null;
  const patch: Partial<DeliveryRunDetail> = {};
  for (const key of MERGEABLE_KEYS) {
    if (payload[key] !== undefined) {
      (patch as JsonMap)[key] = payload[key];
    }
  }
  if (Object.keys(patch).length === 0) return null;

  let merged: DeliveryRunDetail | null = null;
  queryClient.setQueryData<DeliveryRunDetail>(queryKeys.deliveries.run(runId), (prev) => {
    if (!prev) return prev;
    merged = { ...prev, ...patch };
    return merged;
  });
  return merged;
}

/**
 * Owns the SSE lifecycle for one run's live progress:
 *   - opens while `enabled` (the page passes "run known and not terminal");
 *   - merges each frame into the cached run detail so the header, rail and
 *     overview update without a request, and nudges the timeline read at most
 *     every 5s;
 *   - reconnects with capped, jittered backoff (refreshing the token once on
 *     401/403, honouring Retry-After on 429), then reports `fallback` so the
 *     detail hook's 4s poll takes over — the view never goes stale silently;
 *   - closes itself when a frame marks the run terminal, invalidating the run's
 *     subtree and the list so the final reconciled state is re-read.
 */
export function useDeliveryRunStream(runId: string, enabled = true): RunStreamStatus {
  const queryClient = useQueryClient();
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));
  // Status is keyed by run so a stale value from a previous run never leaks into
  // a new one; it is only ever written from stream callbacks / timers (never
  // synchronously inside the effect body).
  const [state, setState] = useState<{ runId: string; status: RunStreamStatus }>({
    runId,
    status: 'connecting',
  });

  // Observe (never fetch) the cached run detail: the stream opens once the run
  // is known and closes for good when it turns terminal. The detail hook owns
  // the actual fetching, so this observer is `enabled: false`.
  const { data: run } = useQuery({
    queryKey: queryKeys.deliveries.run(runId),
    queryFn: () => getDeliveryRun(runId),
    enabled: false,
  });
  const active = enabled && run !== undefined && !run.isTerminal;

  useEffect(() => {
    if (!active || !isAuthed || !runId) return;

    let cancelled = false;
    let attempts = 0;
    let controller: AbortController | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let openedAt: number | null = null;
    let authRefreshed = false;
    let lastSideInvalidate = 0;

    const safeSetStatus = (next: RunStreamStatus) => {
      if (!cancelled) setState({ runId, status: next });
    };

    const finishTerminal = () => {
      cancelled = true;
      controller?.abort();
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.deliveries.all, 'runs', runId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.deliveries.all, 'runs', 'list'],
      });
      setState({ runId, status: 'idle' });
    };

    const wasStable = () => openedAt !== null && Date.now() - openedAt >= MIN_STABLE_CONNECTION_MS;

    const scheduleReconnect = (delayMs?: number) => {
      if (cancelled) return;
      if (attempts >= MAX_RECONNECT_ATTEMPTS) {
        safeSetStatus('fallback');
        return;
      }
      attempts += 1;
      const base = Math.min(MAX_RECONNECT_DELAY_MS, BASE_RECONNECT_DELAY_MS * 2 ** (attempts - 1));
      safeSetStatus('connecting');
      reconnectTimeout = setTimeout(connect, delayMs ?? jitter(base));
    };

    const handleError = (err: Error) => {
      if (wasStable()) attempts = 0;
      openedAt = null;
      if (isRateLimitError(err)) {
        const hint = err.retryAfterSeconds;
        scheduleReconnect(
          hint !== undefined ? Math.min(hint * 1000, RATE_LIMIT_RETRY_AFTER_CAP_MS) : undefined,
        );
        return;
      }
      if (isAuthError(err) && !authRefreshed) {
        authRefreshed = true;
        void refreshAuthToken().then((outcome) => {
          if (cancelled) return;
          if (outcome === 'refreshed') connect();
          else if (outcome === 'transient') scheduleReconnect();
          // 'invalid' → tokens cleared upstream; `isAuthed` flips and this effect tears down.
        });
        return;
      }
      scheduleReconnect();
    };

    function connect(): void {
      if (cancelled) return;
      controller = createDeliveryRunStream({
        runId,
        onOpen: () => {
          openedAt = Date.now();
          authRefreshed = false;
          safeSetStatus('live');
        },
        onEvent: (event) => {
          const merged = applyFrame(queryClient, runId, event);
          const now = Date.now();
          if (now - lastSideInvalidate >= SIDE_INVALIDATE_MS) {
            lastSideInvalidate = now;
            void queryClient.invalidateQueries({
              queryKey: queryKeys.deliveries.runTimeline(runId),
            });
          }
          if (merged?.isTerminal) finishTerminal();
        },
        onComplete: () => {
          // Server closed cleanly. If the run is done the detail poll will confirm it;
          // otherwise reconnect (a stable stream earns a fresh budget).
          if (wasStable()) attempts = 0;
          openedAt = null;
          scheduleReconnect();
        },
        onError: handleError,
      });
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      controller?.abort();
    };
  }, [active, isAuthed, runId, queryClient]);

  if (!active) return 'idle';
  return state.runId === runId ? state.status : 'connecting';
}
